import { Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Tooltip } from "@mui/material";
import { FiltrosContainer } from "./styles";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { filtrosPedidosSchema } from "../../../../schemas/filtrosPedidosSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import { FileDownloadOutlined, Search } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { useEffect } from "react";
import { TipoPedido } from "../../../../enum/TipoPedido";
import { useLetras } from "../../../../../core/hooks/useLetras";
import { IMarketplace } from "../../../../../marketplace/interfaces/IMarketplace";
import { TipoMarketplace, TipoMarketplaceTitulo } from "../../../../../marketplace/enum/TipoMarketplace";

interface FiltrosListagemPedidosProps {
    marketplaces?: IMarketplace[];
    loadingMarketplaces?: boolean;
    onExportar?: () => void;
    loadingExportacao?: boolean;
}

export const FiltrosListagemPedidos = ({ marketplaces, loadingMarketplaces, onExportar, loadingExportacao }: FiltrosListagemPedidosProps) => {
    const { setValue, control, handleSubmit, register, formState: { errors } } = useForm({
        resolver: yupResolver(filtrosPedidosSchema),
        reValidateMode: 'onChange'
    });

    const { dataEua } = useDataHora();
    const { titulo } = useLetras();
    const [searchParams, setSearchParams] = useSearchParams();

    const limpar = () => {
        setValue('numeroPedido', undefined);
        setValue('tipos', [TipoPedido.delivery, TipoPedido.retirada, TipoPedido.simples, TipoPedido.mesa]);
        setValue('dataInicio', moment());
        setValue('dataFim', moment());
        setValue('marketplace', null);
        setValue('tipoFiltroPorData', 'emissao');

        setSearchParams(state => {
            state.delete('numeroPedido');
            state.delete('marketplace');
            state.delete('tipoFiltroPorData');
            state.set('dataInicio', dataEua());
            state.set('dataFim', dataEua());
            state.set('tipos', [
                TipoPedido.delivery,
                TipoPedido.retirada,
                TipoPedido.simples,
                TipoPedido.mesa
            ].join(','));
            return state;
        });
    };

    const filtrar = (filtros: FieldValues) => {
        setSearchParams(state => {
            if (filtros.numeroPedido) {
                state.set('numeroPedido', filtros.numeroPedido);
            } else {
                state.delete('numeroPedido');
            }
            if (filtros.dataInicio) state.set('dataInicio', dataEua(filtros.dataInicio));
            if (filtros.dataFim) state.set('dataFim', dataEua(filtros.dataFim));
            if (filtros.tipos) state.set('tipos', filtros.tipos?.join(','));
            if (filtros.marketplace) {
                state.set('marketplace', filtros.marketplace);
            } else {
                state.delete('marketplace');
            }
            state.set('tipoFiltroPorData', filtros.tipoFiltroPorData ?? 'emissao');
            return state;
        });
    };

    useEffect(() => {
        if (searchParams.get('numeroPedido')) {
            setValue('numeroPedido', searchParams.get('numeroPedido'));
        }

        if (searchParams.get('tipos')) {
            setValue('tipos', searchParams.get('tipos')?.split(','));
        } else {
            setSearchParams(state => {
                state.set('tipos', [
                    TipoPedido.delivery,
                    TipoPedido.retirada,
                    TipoPedido.simples,
                    TipoPedido.mesa
                ].join(','));
                return state;
            });
        }

        if (searchParams.get('dataInicio')) {
            setValue('dataInicio', moment(searchParams.get('dataInicio')));
        } else {
            setSearchParams(state => { state.set('dataInicio', dataEua()); return state; });
        }

        if (searchParams.get('dataFim')) {
            setValue('dataFim', moment(searchParams.get('dataFim')));
        } else {
            setSearchParams(state => { state.set('dataFim', dataEua()); return state; });
        }

        if (searchParams.get('marketplace')) {
            setValue('marketplace', searchParams.get('marketplace'));
        }

        setValue('tipoFiltroPorData', searchParams.get('tipoFiltroPorData') ?? 'emissao');
    }, [searchParams]);

    return (
        <FiltrosContainer>
            <form onSubmit={handleSubmit(filtrar)} style={{ width: '100%' }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        columnGap: '1rem',
                        rowGap: '0.35rem',
                        alignItems: 'center',
                    }}
                >
                    {/* ── Linha 1: inputs ── */}

                    {/* Col 1 */}
                    <TextField
                        fullWidth
                        label="Nº pedido"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        placeholder="Ex: 123"
                        {...register('numeroPedido')}
                        helperText={<>{errors.numeroPedido?.message}</>}
                        error={!!errors.numeroPedido}
                    />

                    {/* Col 2 */}
                    <Controller
                        defaultValue={moment()}
                        name="dataInicio"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                format="DD/MM/YYYY"
                                label="Data início"
                                sx={{ width: '100%' }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        error: !!errors.dataInicio,
                                        helperText: <>{errors?.dataInicio?.message}</>,
                                    },
                                }}
                                value={moment(field.value)}
                                defaultValue={moment()}
                                onChange={(date) => field.onChange(moment(date))}
                            />
                        )}
                    />

                    {/* Col 3 */}
                    <Controller
                        defaultValue={moment()}
                        name="dataFim"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                format="DD/MM/YYYY"
                                label="Data final"
                                sx={{ width: '100%' }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        error: !!errors.dataFim,
                                    },
                                }}
                                defaultValue={moment()}
                                value={moment(field.value)}
                                onChange={(date) => field.onChange(moment(date))}
                            />
                        )}
                    />

                    {/* Col 4 */}
                    <Controller
                        name="tipos"
                        control={control}
                        defaultValue={[TipoPedido.delivery, TipoPedido.retirada, TipoPedido.simples, TipoPedido.mesa]}
                        render={({ field }) => (
                            <FormControl fullWidth size="small" error={!!errors.tipos}>
                                <InputLabel id="tipos-label" shrink error={!!errors.tipos}>Contextos</InputLabel>
                                <Select
                                    labelId="tipos-label"
                                    label="Contextos"
                                    notched
                                    multiple
                                    value={Array.isArray(field.value) ? field.value : []}
                                    onChange={field.onChange}
                                    renderValue={(selected) => titulo(Array.isArray(selected) ? selected.join(', ') : '')}
                                    MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
                                >
                                    {Object.values(TipoPedido).map((tipo) => (
                                        <MenuItem key={tipo} value={tipo}>
                                            <Checkbox checked={field?.value?.indexOf(tipo) > -1} />
                                            {titulo(tipo)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />

                    {/* Col 5 */}
                    <Controller
                        name="marketplace"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <FormControl fullWidth size="small">
                                <InputLabel id="marketplace-label" shrink>Marketplace</InputLabel>
                                <Select
                                    labelId="marketplace-label"
                                    label="Marketplace"
                                    notched
                                    displayEmpty
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value={TipoMarketplace.PDV}>PDV</MenuItem>
                                    {loadingMarketplaces
                                        ? (
                                            <MenuItem disabled>
                                                <CircularProgress size={14} sx={{ mr: 1 }} />
                                                Carregando...
                                            </MenuItem>
                                        )
                                        : marketplaces?.map((mp) => (
                                            <MenuItem key={mp.uuid ?? mp.tipo} value={mp.tipo}>
                                                {mp.nome || TipoMarketplaceTitulo[mp.tipo]}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        )}
                    />

                    {/* ── Linha 2: radio (cols 2-3) + botões (cols 4-5) ── */}

                    {/* Radio – cols 2-3, alinhado exatamente sob os date pickers */}
                    <Controller
                        name="tipoFiltroPorData"
                        control={control}
                        defaultValue="emissao"
                        render={({ field }) => (
                            <RadioGroup
                                row
                                {...field}
                                sx={{ gridColumn: '2 / 4', gridRow: 2 }}
                            >
                                <FormControlLabel
                                    value="emissao"
                                    control={<Radio size="small" sx={{ p: '2px 6px' }} />}
                                    label="Emissão"
                                    slotProps={{ typography: { variant: 'caption' } }}
                                    sx={{ mr: 2 }}
                                />
                                <FormControlLabel
                                    value="agendamento"
                                    control={<Radio size="small" sx={{ p: '2px 6px' }} />}
                                    label="Agendamento"
                                    slotProps={{ typography: { variant: 'caption' } }}
                                    sx={{ mr: 0 }}
                                />
                            </RadioGroup>
                        )}
                    />

                    {/* Botões – cols 4-5, alinhados à direita */}
                    <Box sx={{ gridColumn: '4 / 6', gridRow: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem' }}>
                        {onExportar && (
                            <>
                                <Tooltip title="Baixa os pedidos em aberto, concluídos e cancelados dos filtros aplicados, com itens e pagamentos">
                                    <span>
                                        <Button
                                            variant="text"
                                            type="button"
                                            onClick={onExportar}
                                            disabled={loadingExportacao}
                                            startIcon={loadingExportacao ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
                                        >
                                            EXPORTAR
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Divider orientation="vertical" flexItem />
                            </>
                        )}
                        <Button variant="outlined" onClick={limpar} type="button">
                            LIMPAR
                        </Button>
                        <Button type="submit" startIcon={<Search />} variant="contained">
                            FILTRAR
                        </Button>
                    </Box>
                </Box>
            </form>
        </FiltrosContainer>
    );
};
